import { useState, useEffect } from 'react';
import { connectZotero, disconnectZotero, getZoteroCollections, getZoteroItems } from '../api';

function SpinnerIcon({ className = 'h-4 w-4' }) {
  return (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function PaperRow({ paper, checked, onChange }) {
  const authors =
    paper.authors?.length > 0
      ? paper.authors.slice(0, 2).join(', ') + (paper.authors.length > 2 ? ' et al.' : '')
      : paper.author || '';

  return (
    <label className="flex items-start gap-3 px-3 py-2.5 hover:bg-[#fafafa] cursor-pointer rounded-md transition-colors">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="mt-0.5 h-4 w-4 rounded cursor-pointer flex-shrink-0 accent-[#171717]"
      />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-[#171717] font-medium leading-snug line-clamp-2">
          {paper.title || 'Untitled'}
        </p>
        <p className="text-xs text-[#808080] mt-0.5">
          {[authors, paper.published_date || paper.year].filter(Boolean).join(' · ')}
        </p>
      </div>
    </label>
  );
}

export default function ZoteroSync({ onImport }) {
  const [connected, setConnected] = useState(false);
  const [connectedUser, setConnectedUser] = useState(null);
  const [userId, setUserId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState('');

  const [collections, setCollections] = useState([]);
  const [expandedCollectionId, setExpandedCollectionId] = useState(null);
  const [collectionItems, setCollectionItems] = useState({});
  const [loadingCollectionId, setLoadingCollectionId] = useState(null);

  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingCollections, setLoadingCollections] = useState(false);

  const [activeTab, setActiveTab] = useState('collections');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [importSuccess, setImportSuccess] = useState('');
  const [importError, setImportError] = useState('');

  useEffect(() => {
    const tryAutoConnect = async () => {
      setLoadingCollections(true);
      try {
        const response = await getZoteroCollections();
        setCollections(response.data.collections || []);
        setConnectedUser(response.data.user || null);
        setConnected(true);
      } catch {
        setConnected(false);
      } finally {
        setLoadingCollections(false);
      }
    };
    tryAutoConnect();
  }, []);

  const handleConnect = async () => {
    if (!userId.trim() || !apiKey.trim()) return;
    setConnecting(true);
    setConnectError('');
    try {
      const response = await connectZotero(userId.trim(), apiKey.trim());
      setConnectedUser(response.data.user || null);
      const colResponse = await getZoteroCollections();
      setCollections(colResponse.data.collections || []);
      setConnected(true);
    } catch (err) {
      setConnectError(err.response?.data?.detail || 'Failed to connect. Check your User ID and API key.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try { await disconnectZotero(); } catch {}
    setConnected(false);
    setConnectedUser(null);
    setCollections([]);
    setItems([]);
    setSelectedIds(new Set());
    setCollectionItems({});
    setExpandedCollectionId(null);
    setImportSuccess('');
    setImportError('');
    setUserId('');
    setApiKey('');
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setSelectedIds(new Set());
    setImportSuccess('');
    setImportError('');

    if (tab === 'articles' && items.length === 0) {
      setLoadingItems(true);
      try {
        const response = await getZoteroItems(null, 50);
        setItems(response.data.items || []);
      } catch (err) {
        setImportError(err.response?.data?.detail || 'Failed to load articles.');
      } finally {
        setLoadingItems(false);
      }
    }
  };

  const handleToggleCollection = async (collection) => {
    const id = collection.id ?? collection.key;
    if (expandedCollectionId === id) { setExpandedCollectionId(null); return; }
    setExpandedCollectionId(id);
    if (!collectionItems[id]) {
      setLoadingCollectionId(id);
      try {
        const response = await getZoteroItems(id);
        setCollectionItems((prev) => ({ ...prev, [id]: response.data.items || [] }));
      } catch {
        setCollectionItems((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setLoadingCollectionId(null);
      }
    }
  };

  const visibleItems =
    activeTab === 'articles'
      ? items
      : expandedCollectionId
      ? collectionItems[expandedCollectionId] || []
      : [];

  const toggleItem = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(visibleItems.map((p) => p.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const handleImport = () => {
    const toImport = visibleItems.filter((p) => selectedIds.has(p.id));
    if (toImport.length === 0) return;
    onImport(toImport);
    setImportSuccess(`${toImport.length} paper${toImport.length !== 1 ? 's' : ''} added to workspace`);
    setImportError('');
    setSelectedIds(new Set());
  };

  if (loadingCollections) {
    return (
      <div className="flex items-center justify-center py-12 text-[#808080] gap-2">
        <SpinnerIcon />
        <span className="text-sm">Checking Zotero connection...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[#171717]" style={{ letterSpacing: '-1.28px' }}>Zotero Sync</h2>
        <p className="text-sm text-[#4d4d4d] mt-1">
          Import papers from your Zotero library directly into your workspace.
        </p>
      </div>

      {!connected ? (
        <div className="bg-white rounded-lg p-5 space-y-4" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
          <p className="text-sm text-[#4d4d4d]">
            Zotero is a free reference manager. Connect your account to import papers from your library.
          </p>

          <div>
            <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
              Zotero User ID
            </label>
            <input
              type="text"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              placeholder="e.g. 1234567"
              className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              disabled={connecting}
            />
            <p className="mt-1 text-xs text-[#808080]">
              Find your User ID at{' '}
              <a href="https://www.zotero.org/settings/keys" target="_blank" rel="noopener noreferrer" className="text-[#171717] hover:underline">
                zotero.org/settings
              </a>{' '}
              — it's the number shown under your username.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#171717] mb-1" style={{ letterSpacing: '-0.32px' }}>
              API Key
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Your Zotero API key"
              className="w-full rounded-md px-3 py-2 text-sm text-[#171717] placeholder-[#808080] bg-white focus:outline-none focus:ring-2 focus:ring-[#0a72ef]"
              style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
              disabled={connecting}
            />
            <p className="mt-1 text-xs text-[#808080]">
              Generate an API key at{' '}
              <a href="https://www.zotero.org/settings/keys" target="_blank" rel="noopener noreferrer" className="text-[#171717] hover:underline">
                zotero.org/settings/keys
              </a>
              . Enable read access to your library.
            </p>
          </div>

          {connectError && (
            <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{connectError}</p>
          )}

          <button
            onClick={handleConnect}
            disabled={!userId.trim() || !apiKey.trim() || connecting}
            className="w-full px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                       hover:bg-[#2e2e2e] disabled:opacity-50 disabled:cursor-not-allowed
                       transition-colors flex items-center justify-center gap-2"
          >
            {connecting ? (
              <><SpinnerIcon />Connecting...</>
            ) : (
              'Connect Zotero'
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Connection status */}
          <div
            className="bg-white rounded-lg px-4 py-3 flex items-center justify-between"
            style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}
          >
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700" style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}>
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                Connected
              </span>
              {connectedUser && (
                <span className="text-sm text-[#4d4d4d]">
                  {connectedUser.username ? (
                    <>{connectedUser.username} <span className="text-[#808080]">· ID {connectedUser.id}</span></>
                  ) : (
                    <>User ID: {connectedUser.id}</>
                  )}
                </span>
              )}
            </div>
            <button
              onClick={handleDisconnect}
              className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
            >
              Disconnect
            </button>
          </div>

          {/* Tabs + content */}
          <div className="bg-white rounded-lg overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
            <div className="flex" style={{ borderBottom: '1px solid #ebebeb' }}>
              {[
                { id: 'collections', label: 'Collections' },
                { id: 'articles', label: 'All Articles' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium transition-colors"
                  style={{
                    color: activeTab === tab.id ? '#171717' : '#808080',
                    borderBottom: activeTab === tab.id ? '2px solid #171717' : '2px solid transparent',
                    background: activeTab === tab.id ? '#fafafa' : 'transparent',
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="p-3">
              {activeTab === 'collections' && (
                <div>
                  {collections.length === 0 ? (
                    <p className="text-sm text-[#808080] text-center py-6">No collections found in your library.</p>
                  ) : (
                    <div className="space-y-1">
                      {collections.map((col) => {
                        const colId = col.id ?? col.key;
                        const isExpanded = expandedCollectionId === colId;
                        const colPapers = collectionItems[colId] || [];
                        const isLoadingCol = loadingCollectionId === colId;

                        return (
                          <div key={colId} className="rounded-md overflow-hidden" style={{ boxShadow: 'rgba(0,0,0,0.06) 0px 0px 0px 1px' }}>
                            <button
                              onClick={() => handleToggleCollection(col)}
                              className="w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-[#fafafa] transition-colors"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <svg
                                  className={`h-4 w-4 text-[#808080] flex-shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                </svg>
                                <span className="font-medium text-[#171717] truncate">{col.name}</span>
                              </div>
                              {col.num_items != null && (
                                <span className="flex-shrink-0 ml-2 text-xs text-[#808080] rounded-full px-2 py-0.5" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
                                  {col.num_items}
                                </span>
                              )}
                            </button>

                            {isExpanded && (
                              <div style={{ borderTop: '1px solid #ebebeb' }}>
                                {isLoadingCol ? (
                                  <div className="flex items-center justify-center py-4 gap-2 text-[#808080] text-sm">
                                    <SpinnerIcon />
                                    Loading papers...
                                  </div>
                                ) : colPapers.length === 0 ? (
                                  <p className="text-sm text-[#808080] text-center py-4">No journal articles in this collection.</p>
                                ) : (
                                  <div style={{ borderTop: '1px solid #ebebeb' }}>
                                    {colPapers.map((paper) => (
                                      <PaperRow
                                        key={paper.id}
                                        paper={paper}
                                        checked={selectedIds.has(paper.id)}
                                        onChange={() => toggleItem(paper.id)}
                                      />
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'articles' && (
                <div>
                  {loadingItems ? (
                    <div className="flex items-center justify-center py-8 gap-2 text-[#808080] text-sm">
                      <SpinnerIcon />
                      Loading articles...
                    </div>
                  ) : items.length === 0 ? (
                    <p className="text-sm text-[#808080] text-center py-6">No journal articles found in your library.</p>
                  ) : (
                    <div>
                      {items.map((paper) => (
                        <PaperRow
                          key={paper.id}
                          paper={paper}
                          checked={selectedIds.has(paper.id)}
                          onChange={() => toggleItem(paper.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {visibleItems.length > 0 && (
            <div className="bg-white rounded-lg px-4 py-3 space-y-3" style={{ boxShadow: 'rgba(0,0,0,0.08) 0px 0px 0px 1px' }}>
              <div className="flex items-center gap-3 text-xs text-[#808080]">
                <button onClick={selectAll} className="text-[#171717] font-medium hover:underline">Select all</button>
                <span>/</span>
                <button onClick={deselectAll} className="text-[#171717] font-medium hover:underline">Deselect all</button>
                {selectedIds.size > 0 && <span>{selectedIds.size} selected</span>}
              </div>

              {importSuccess && (
                <p className="text-sm text-green-700 bg-green-50 rounded px-3 py-2 font-medium">{importSuccess}</p>
              )}
              {importError && (
                <p className="text-sm text-red-600 bg-red-50 rounded px-3 py-2">{importError}</p>
              )}

              <button
                onClick={handleImport}
                disabled={selectedIds.size === 0}
                className="w-full px-4 py-2 bg-[#171717] text-white rounded-md font-medium text-sm
                           hover:bg-[#2e2e2e] disabled:opacity-40 disabled:cursor-not-allowed
                           transition-colors"
              >
                Import {selectedIds.size > 0 ? `${selectedIds.size} selected paper${selectedIds.size !== 1 ? 's' : ''}` : 'selected papers'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
